import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRight,
  faSync,
  faFileSearch,
} from '@fortawesome/pro-solid-svg-icons'
import { Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { EvaluationStatesEnum } from 'enum/evaluation-states.enum'
import MandatoryQuestion from 'components/ui/mandatory-question/MandatoryQuestion'
import Button, {
  ButtonVariant,
  ButtonSizes,
} from 'design-system/button/src/Button'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { IEvaluationQuestion } from 'models/evaluation.model'
import { IMainState } from 'store/states'
import { useIsDesktop } from 'helpers'
import { evaluationHasResponse } from 'helpers/validators.helper'
import { useLesson } from 'contexts'
import Evaluation from './Evaluation'
import styles from './ContentBlockEvaluation.module.scss'

export const PrepareEvaluation = (
  data: IEvaluationQuestion,
  t: any,
  location: any,
  setOnEvaluation: any,
  canEdit: boolean,
  refArray: any,
  count: any,
  lessonId: number
) => {
  const { onCheck, onPreview, inProcess } = EvaluationStatesEnum
  const state =
    location.pathname.indexOf('preview') > -1
      ? onPreview
      : location.pathname.indexOf('check') > -1
      ? onCheck
      : inProcess

  const isDesktop = useIsDesktop()
  const { id, blockInstanceableId, config, questions, results } = data
  const [showModalResults, setShowModalResults] = useState(false)
  const { lesson } = useSelector(({ lmsLessons }: IMainState) => lmsLessons)
  const hasResponse = evaluationHasResponse(data)
  const { submit, setSubmit } = useLesson()
  const handleOpenPreview = () => {
    window.open(`/administration/evaluation/${id}/preview`)
  }

  const handleShowModalResults = () => {
    if (isDesktop) {
      window.open(`/evaluation/${id}/check`)
    } else {
      setShowModalResults(true)
    }
  }

  const handleOnClose = () => {
    setShowModalResults(false)
  }

  const { maxRetries } = { ...config }
  const remainingAttempts = (maxRetries || 0) - results.attemps

  const disableButton = () => {
    if (
      (!remainingAttempts ||
        !canEdit ||
        questions?.length === results.correct) &&
      state !== onPreview
    )
      return true
    return false
  }

  return (
    <div
      id="evaluation"
      key={'blockEvaluation/' + id + '-' + blockInstanceableId}
      className={'mb-4'}
    >
      <div className={styles.tries}>
        {!results.attemps && (
          <>
            <b>
              {t('lessons.contentBlocks.evaluationBlock.attemptsAllowed')} :
            </b>{' '}
            {config?.maxRetries}
          </>
        )}
      </div>
      <div className="mt-2 d-flex">
        <CommonToolTip
          text={t('lessons.contentBlocks.evaluationBlock.noRemainingAttempts')}
          position="top"
          className={styles.tabsTooltip}
          disabled={
            remainingAttempts > 0 ||
            state === onPreview ||
            questions?.length === results.correct
          }
        >
          <div>
            <Button
              className={`m-0 ${!hasResponse ? 'new' : 'repeat'}`}
              disabled={disableButton()}
              onClick={() =>
                state === onPreview
                  ? handleOpenPreview()
                  : remainingAttempts && setOnEvaluation(results.attemps)
              }
              variant={ButtonVariant.Success}
              size={ButtonSizes.Small}
            >
              {!results.attemps || state === onPreview ? (
                <>
                  <FontAwesomeIcon icon={faArrowRight} className="mr-2" />
                  {t('lessons.contentBlocks.evaluationBlock.buttons.start')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSync} className="mr-2" />
                  {t('lessons.contentBlocks.evaluationBlock.buttons.restart')}
                </>
              )}
            </Button>
          </div>
        </CommonToolTip>
        {state === inProcess && config?.showResults && !!results.attemps && (
          <>
            <Button
              className="m-0 ml-2"
              onClick={handleShowModalResults}
              variant={ButtonVariant.Primary}
              outline
              size={ButtonSizes.Small}
            >
              <FontAwesomeIcon icon={faFileSearch} className="mr-2" />
              {t('lessons.contentBlocks.evaluationBlock.buttons.check')}
            </Button>
          </>
        )}
      </div>
      {showModalResults && (
        <Modal
          show={showModalResults}
          onClose={handleOnClose}
          dialogClassName={'modal-fullscreen'}
        >
          {!!(lesson && lesson.blockInstances && id) && (
            <Modal.Body>
              <Evaluation
                state={EvaluationStatesEnum.onCheck}
                courseId={lesson.courseId}
                blockInstances={lesson.blockInstances}
                lessonId={lessonId}
                evaluationId={id}
                canEdit={canEdit}
                setOnEvaluation={() => setOnEvaluation(false)}
                onClose={handleOnClose}
                onSuccess={() => setSubmit(false)}
              />
            </Modal.Body>
          )}
        </Modal>
      )}
      <div className="mt-2">
        <MandatoryQuestion
          customText={t('lessons.contentBlocks.evaluationBlock.mandatoryError')}
          show={submit && !hasResponse}
        />
      </div>
    </div>
  )
}
